import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { usePage } from '@inertiajs/react'
import AsyncSelect from 'react-select/async'
import { CustomOption, CustomSingleValue, customSelectStyles } from '@features/permissions/components/PermissionsModal/UserSelectComponents'

interface UserOption {
  value: string
  label: string
  user: any
}

interface UserFilterInputProps {
  fieldKey: string
  onSubmit: (value: string, valueLabel: string) => void
}

export function UserFilterInput({ fieldKey, onSubmit }: UserFilterInputProps) {
  const { t } = useTranslation()
  const { user } = usePage<{ user: any }>().props
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null)

  // Pre-populate with current user for owner filter
  useEffect(() => {
    if (fieldKey === 'owner_id' && user) {
      const currentUserOption: UserOption = {
        value: user.id,
        label: user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : user.email,
        user
      }
      setSelectedUser(currentUserOption)
    }
  }, [fieldKey, user])

  const loadUsers = async (inputValue: string): Promise<UserOption[]> => {
    if (inputValue.length < 2) {
      return []
    }

    try {
      const response = await fetch(`/users/search?q=${encodeURIComponent(inputValue)}`)
      const data = await response.json()

      return data.users.map((u: any) => ({
        value: u.id,
        label: u.first_name && u.last_name
          ? `${u.first_name} ${u.last_name}`
          : u.email,
        user: u
      }))
    } catch (error) {
      console.error('Error loading users:', error)
      return []
    }
  }

  const handleUserChange = (option: UserOption | null) => {
    setSelectedUser(option)
    // Auto-submit when user selects a different user from dropdown
    if (option) {
      onSubmit(option.value, option.label)
    }
  }

  const handleUserApply = () => {
    // Submit the preselected user when button is clicked
    if (selectedUser) {
      onSubmit(selectedUser.value, selectedUser.label)
    }
  }

  return (
    <>
      <AsyncSelect
        cacheOptions
        defaultOptions
        loadOptions={loadUsers}
        value={selectedUser}
        onChange={handleUserChange}
        placeholder={t('permissions.changeOwner.searchPlaceholder')}
        noOptionsMessage={() => t('permissions.noResults')}
        loadingMessage={() => t('permissions.searching')}
        styles={customSelectStyles}
        menuPortalTarget={document.body}
        classNames={{
          menu: () => 'bg-base-100',
          menuList: () => 'bg-base-100'
        }}
        components={{
          Option: CustomOption,
          SingleValue: CustomSingleValue
        }}
        isClearable
        autoFocus
      />
      {selectedUser && (
        <button
          onClick={handleUserApply}
          className="btn btn-primary btn-sm w-full mt-2"
        >
          {t('fileExplorer.filters.addFilter')}
        </button>
      )}
    </>
  )
}
